// CareLogix™ ORACLE — Public Member Auth Client (Phase 23).
// Strictly additive. Wraps `/api/auth/*` and handles cookie + Bearer.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS
// BREAKS THE EMERGENT GOOGLE AUTH FLOW.
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/auth`;

const TOKEN_LS = "carelogix_member_token";
const USER_LS  = "carelogix_member_user";

export const authStorage = {
  getToken() {
    try { return localStorage.getItem(TOKEN_LS); } catch { return null; }
  },
  setToken(v) {
    try {
      if (v) {
        localStorage.setItem(TOKEN_LS, v);
        // Phase 39 — mirror into the canonical ORACLE key so the unified
        // member-session resolver picks up the same JWT no matter which
        // legacy consumer (homepage, membership client, hook) reads first.
        localStorage.setItem("oracle_member_token", v);
      } else {
        localStorage.removeItem(TOKEN_LS);
        localStorage.removeItem("oracle_member_token");
      }
    } catch { /* ignore */ }
    _fireAuthChanged();
  },
  getUser() {
    try {
      const raw = localStorage.getItem(USER_LS);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  },
  setUser(u) {
    try {
      if (u) {
        localStorage.setItem(USER_LS, JSON.stringify(u));
        // Phase 39 — mirror into the canonical ORACLE member profile key.
        localStorage.setItem("oracle_member", JSON.stringify(u));
      } else {
        localStorage.removeItem(USER_LS);
        localStorage.removeItem("oracle_member");
      }
    } catch { /* ignore */ }
    _fireAuthChanged();
  },
  clear() {
    try {
      localStorage.removeItem(TOKEN_LS);
      localStorage.removeItem(USER_LS);
      localStorage.removeItem("oracle_member_token");
      localStorage.removeItem("oracle_member");
    } catch { /* ignore */ }
    _fireAuthChanged();
  },
};

// Phase 30A — broadcast an in-page event whenever the auth token/user changes
// so the MembershipContext (and any other listeners) re-fetch the caller's
// tier-feature profile immediately, without waiting for a manual refresh.
function _fireAuthChanged() {
  try {
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      window.dispatchEvent(new Event("carelogix:auth-changed"));
    }
  } catch { /* ignore */ }
}

// Axios instance — includes credentials for cookie + Bearer for stateless calls
const http = axios.create({
  baseURL: API,
  timeout: 25000,
  withCredentials: true,
});
http.interceptors.request.use((c) => {
  const t = authStorage.getToken();
  c.headers = { ...(c.headers || {}) };
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

export const authApi = {
  status:    () => http.get("/status"),
  register:  (payload) => http.post("/register", payload),
  login:     (payload) => http.post("/login", payload),
  guest:     () => http.post("/guest", {}),
  me:        () => http.get("/me"),
  logout:    () => http.post("/logout", {}),
  oauthStart:  (provider, redirect_origin) =>
                  http.post("/oauth/start", { provider, redirect_origin }),
  oauthComingSoon: (provider, identifier) =>
                  http.post("/oauth/coming-soon", { provider, identifier: identifier || null }),
  googleSession: (session_id) => http.post("/google/session", { session_id }),
  auditRecent:   (limit = 50) => http.get("/audit/recent", { params: { limit } }),
};

// Webview / restricted preview detection — used to nudge users into a real browser
// for the Google OAuth flow (Emergent Auth refuses to authenticate inside webviews).
export function isLikelyRestrictedWebview(ua = (typeof navigator !== "undefined" ? navigator.userAgent : "")) {
  if (!ua) return false;
  const lo = String(ua).toLowerCase();
  // Common in-app browsers / restricted previews
  return (
    lo.includes("instagram") ||
    lo.includes("fbav") ||
    lo.includes("fban") ||
    lo.includes("messenger") ||
    lo.includes("line/") ||
    lo.includes("snapchat") ||
    lo.includes("tiktok") ||
    lo.includes("musical_ly") ||
    lo.includes("twitter") ||
    lo.includes("linkedinapp") ||
    lo.includes("micromessenger") || // WeChat
    /\bwv\)/.test(lo) ||             // Android system WebView marker
    /; wv;/.test(lo) ||
    (lo.includes("iphone") && !lo.includes("safari") && !lo.includes("crios") && !lo.includes("fxios"))
  );
}

// Helper that computes the redirect target for Google sign-in.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS
// BREAKS THE EMERGENT GOOGLE AUTH FLOW.
export function googleSignInUrl() {
  // Per Emergent Auth playbook: redirect to the live app origin + a target path,
  // then read `#session_id=` from the URL fragment when the user returns.
  const origin = window.location.origin;
  const redirect = `${origin}/`;
  return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
}

export default authApi;
